<script setup lang="ts">
    import { computed, toRef } from 'vue'

    import type { SubSurveysManagerDialogViewModel } from '@Evaluation/CriteriaSurveys/Components/SubSurveysManagerDialog'

    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: SubSurveysManagerDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'include-sub-survey', masterSurveyId: number, surveyId: number, voteStrength: number): void
        (e: 'change-vote-strength', masterSurveyId: number, surveyId: number, voteStrength: number): void
        (e: 'exclude-sub-survey', masterSurveyId: number, surveyId: number): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const opened = computed(() => vm.value?.opened ?? false)

    const initialized = computed(() => vm.value.rm != null)

    const masterSurveyId = computed(() => vm.value.masterSurveyId)
    const masterSurveyName = computed(() => vm.value.masterSurveyName)

    const entries = computed(() => vm.value.rm?.Entries ?? [])

    // ---------------- actions ----------------

    function emitIncludeSubSurvey(surveyId: number, voteStrength: number)
    {
        const s = entries.value.find(a => a.Id === surveyId)
        if (!s) return

        if (s.IsIncluded === false)
        {
            emit('include-sub-survey', masterSurveyId.value, surveyId, voteStrength)
        }
        else if (s.IsIncluded === true && s.VoteStrength !== voteStrength)
        {
            emit('change-vote-strength', masterSurveyId.value, surveyId, voteStrength)
        }
    }

    function emitExcludeSubSurvey(surveyId: number)
    {
        const s = entries.value.find(a => a.Id === surveyId)
        if (!s) return

        if (s.IsIncluded === true)
        {
            emit('exclude-sub-survey', masterSurveyId.value, surveyId)
        }
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened === true">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content" style="height: calc(100vh - 50px);">
                    <div class="modal-header">
                        <h5 class="modal-title">Combined panels</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h2 v-if="initialized === false"><i class="fa fa-spinner fa-spin"></i> Loading...</h2>
                        <div v-if="initialized">
                            <div class="alert alert-info" role="alert">
                                You can include another panel as if it were a respondent.
                                This lets you, for example, combine and compare votes from different groups of people.
                            </div>

                            <div>
                                <table class="table table-hover">
                                    <tbody>
                                        <tr v-for="s in entries" :key="s.Id">
                                            <td>{{s.Name}}</td>
                                            <td style="width: 20%;">
                                                <div class="dropdown">
                                                    <a class="text-primary" data-toggle="dropdown">
                                                        {{ s.IsIncluded ? `Vote strength ${s.VoteStrength}` : "Not included" }}
                                                    </a>
                                                    <div class="dropdown-menu" role="menu">
                                                        <a class="dropdown-item" @click="emitExcludeSubSurvey(s.Id)">Not included</a>
                                                        <a v-for="num in 11" :key="num" class="dropdown-item" @click="emitIncludeSubSurvey(s.Id, num - 1)">
                                                            Vote strength: {{ num - 1 }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style="width: 20%;">
                                                <div v-if="s.IsIncluded" class="progress" style="margin: 0;">
                                                    <div :style="{ width: s.Progress + '%' }" :title="s.Progress" class="progress-bar progress-bar-success progress-bar-striped" role="progressbar"></div>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>